import {
  SettingsAssessmentType,
  SettingsComplianceType,
} from 'types/ComplianceAndAssessmentTypeSettings';

export enum RowStatus {
  'showAll',
  'matchingSearchTerm',
  'collapsed',
}

export const calculateSearchMatches = ({
  complianceType,
  assessmentTypes,
  searchTerm,
}: {
  complianceType: SettingsComplianceType;
  assessmentTypes: SettingsAssessmentType[];
  searchTerm: string;
}): {
  complianceTypeMatches: boolean;
  matchingAssessmentTypes: SettingsAssessmentType[];
} => {
  const complianceTypeMatches = complianceType.name
    .toLocaleLowerCase()
    .includes(searchTerm);
  const matchingAssessmentTypes = assessmentTypes.filter((aseType) =>
    aseType.name.toLocaleLowerCase().includes(searchTerm),
  );
  return {
    complianceTypeMatches,
    matchingAssessmentTypes,
  };
};

export const getRowStatusFromCollapseAndShowState = ({
  hasSearchTerm,
  hasMatchingAssessmentTypes,
  isComplianceCollapsed,
  showAllAssessmentTypes,
}: {
  hasSearchTerm: boolean;
  hasMatchingAssessmentTypes: boolean;
  isComplianceCollapsed: boolean;
  showAllAssessmentTypes: boolean;
}): RowStatus => {
  // Collapse wins even if this row had previously been set to show all
  if (isComplianceCollapsed) {
    return RowStatus.collapsed;
  }
  // Show all if there's no search
  if (!hasSearchTerm) {
    return RowStatus.showAll;
  }
  // Show all when explicitly requested or when no assessment types matched
  if (showAllAssessmentTypes || !hasMatchingAssessmentTypes) {
    return RowStatus.showAll;
  }
  return RowStatus.matchingSearchTerm;
};

/*
 * Returns new list state on search change
 */
export const calculateSearchState = ({
  complianceTypes,
  sortedAssessmentTypesByComplianceTypeId,
  searchTerm,
}: {
  complianceTypes: Record<string, SettingsComplianceType>;
  sortedAssessmentTypesByComplianceTypeId: Record<
    string,
    SettingsAssessmentType[]
  >;
  searchTerm: string;
}) => {
  const newCollapsedState: Record<string, boolean> = {};
  const newShowAllState: Record<string, boolean> = {};
  const newMatchingComplianceTypeIds: string[] = [];
  const newMatchingAssessmentTypeIds: string[] = [];

  if (searchTerm) {
    Object.values(complianceTypes).forEach((complianceType) => {
      const assessmentTypes =
        sortedAssessmentTypesByComplianceTypeId[complianceType.id] ?? [];
      const { complianceTypeMatches, matchingAssessmentTypes } =
        calculateSearchMatches({
          complianceType,
          assessmentTypes,
          searchTerm,
        });
      if (matchingAssessmentTypes.length) {
        // Matched assessment types - show compliance and matches assessment types
        newCollapsedState[complianceType.id] = false;
        newMatchingComplianceTypeIds.push(complianceType.id);
        newMatchingAssessmentTypeIds.push(
          ...matchingAssessmentTypes.map((aseType) => aseType.id),
        );
      } else if (complianceTypeMatches) {
        // Matched only compliance type - hide assessment types
        newCollapsedState[complianceType.id] = true;
        newMatchingComplianceTypeIds.push(complianceType.id);
      }
    });
  }

  return {
    newHasSearchTerm: !!searchTerm,
    newCollapsedState,
    newShowAllState,
    newMatchingComplianceTypeIds,
    newMatchingAssessmentTypeIds,
  };
};
